import last from 'lodash/last';
import mapValues from 'lodash/mapValues';
import moment from 'moment';

import {
  EXPERIMENT_NAME_PROPERTY,
  EXPERIMENT_STARTED_EVENT,
} from 'apps/experiment-reporting/src/experiment-reporting-app/constants';
import {
  Experiment,
  ExperimentCollectionType,
  ExperimentMetric,
  ExperimentMetricResults,
  ExperimentMetricResultsFormat,
  ExperimentTestingModel,
  StatisticallySignificant,
  StatsigResults,
} from 'apps/experiments/src/types';
import {TimeClause} from 'apps/signal/src/models/clause';
import {
  ALL_MATH_TYPES,
  DATASET_MIXPANEL,
  EXPERIMENT_PROPERTY_PREFIX,
  PropertyType,
  RESULTS_OVERALL_SUM_KEY,
} from 'common/report/constants';
import {TIME_UNITS} from 'common/report/insights/models/constants';
import {DATE_RANGE_TYPE} from 'common/report/insights/models/time-clause';
import {_getDefaultInsightsSorting} from 'common/report/insights/util';
import {
  FilterClause,
  GroupClause,
  InsightsQueryResults,
  InsightsSeriesTree,
  InsightsSortConfig,
  ShowClause,
} from 'common/report/insights/util/types';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  InsightsBookmarkParams,
  InsightsResourceType,
  MetricType,
  ValueRepresentationType,
} from 'common/types/reports/bookmark';
import {StringOperator} from 'common/widgets/property-filter-menu/models/types';
import {FeatureFlagApiResponse} from 'app_api/projects/feature_flags/types';

const TIME_CLAUSE_DATE_FORMAT = `YYYY-MM-DDTHH:mm:ss`;

export function createEventDetectionBookmarkParams({
  experiment,
  featureFlag,
  experimentNameProperty = EXPERIMENT_NAME_PROPERTY,
}: {
  experiment: Experiment;
  featureFlag?: FeatureFlagApiResponse;
  experimentNameProperty?: string;
}): InsightsBookmarkParams | undefined {
  const experimentKey = _getExperimentKey(experiment, featureFlag);
  if (!experimentKey) {
    return undefined;
  }

  const show: ShowClause[] = [
    {
      behavior: {
        type: MetricType.Event,
        resourceType: InsightsResourceType.Events,
        dataGroupId: undefined,
        dataset: DATASET_MIXPANEL,
        filters: [],
        name: EXPERIMENT_STARTED_EVENT,
        profileType: undefined,
        search: ``,
      },
      measurement: {
        math: ALL_MATH_TYPES.TOTAL,
        cumulative: false,
        perUserAggregation: undefined,
        property: undefined,
        rolling: undefined,
      },
    },
  ];

  const filter: FilterClause[] = [
    {
      dataset: DATASET_MIXPANEL,
      value: experimentNameProperty,
      resourceType: InsightsResourceType.Events,
      profileType: null,
      search: ``,
      dataGroupId: undefined,
      filterType: PropertyType.String,
      defaultType: PropertyType.String,
      filterOperator: StringOperator.Equals,
      filterValue: [experimentKey],
      propertyObjectKey: undefined,
    },
  ];

  return {
    sections: {
      cohorts: [],
      formula: [],
      show,
      filter,
      time: [
        {
          dateRangeType: DATE_RANGE_TYPE.RELATIVE,
          unit: TIME_UNITS.DAY,
          window: {
            value: 365,
            unit: TIME_UNITS.DAY,
          },
          value: null,
        },
      ],
    },
    sorting: _buildExperimentSorting(),
    columnWidths: {
      bar: {},
    },
    displayOptions: {
      chartType: ChartType.Line,
      plotStyle: ChartPlotStyle.Stacked,
      analysis: AnalysisType.Linear,
      value: ValueRepresentationType.Absolute,
    },
  };
}

export function createExposuresBookmarkParams({
  experiment,
  featureFlag,
  experimentEventName = EXPERIMENT_STARTED_EVENT,
  experimentNameProperty = EXPERIMENT_NAME_PROPERTY,
}: {
  experiment?: Experiment;
  featureFlag?: FeatureFlagApiResponse;
  experimentEventName?: string;
  experimentNameProperty?: string;
}): InsightsBookmarkParams | undefined {
  if (!experiment?.start_date) {
    return undefined;
  }

  const experimentKey = _getExperimentKey(experiment, featureFlag);
  if (!experimentKey) {
    return undefined;
  }

  const show: ShowClause[] = [
    {
      behavior: {
        type: MetricType.Event,
        resourceType: InsightsResourceType.Events,
        dataGroupId: undefined,
        dataset: DATASET_MIXPANEL,
        filters: [],
        name: experimentEventName,
        profileType: undefined,
        search: ``,
      },
      measurement: {
        math: ALL_MATH_TYPES.CUMULATIVE_UNIQUE,
        cumulative: false,
        perUserAggregation: undefined,
        property: undefined,
        rolling: undefined,
      },
    },
  ];

  const filter: FilterClause[] = [
    {
      dataset: DATASET_MIXPANEL,
      value: experimentNameProperty,
      resourceType: InsightsResourceType.Events,
      profileType: null,
      search: ``,
      dataGroupId: undefined,
      filterType: PropertyType.String,
      defaultType: PropertyType.String,
      filterOperator: StringOperator.Equals,
      filterValue: [experimentKey],
      propertyObjectKey: undefined,
    },
  ];

  return {
    sections: {
      cohorts: [],
      formula: [],
      show,
      filter,
      group: buildExperimentGroup(experiment, featureFlag),
      time: buildExperimentTime(experiment),
    },
    sorting: _buildExperimentSorting(),
    columnWidths: {
      bar: {},
    },
    displayOptions: {
      chartType: ChartType.Line,
      plotStyle: ChartPlotStyle.Stacked,
      analysis: AnalysisType.Linear,
      value: ValueRepresentationType.Absolute,
    },
  };
}

export type InsightsBookmarkParamsWithMetricId = InsightsBookmarkParams & {metricId: string};

export function createStatsigBookmarkParams({
  experiment,
  metric,
  controlKey,
  exposures,
  featureFlag,
  additionalFilters = [],
}: {
  experiment: Experiment;
  metric: ExperimentMetric;
  controlKey: string;
  exposures: Record<string, number>;
  featureFlag?: FeatureFlagApiResponse;
  additionalFilters?: FilterClause[];
}): InsightsBookmarkParamsWithMetricId | undefined {
  if (!experiment.start_date || !metric.definition) {
    return undefined;
  }

  const experimentKey = _getExperimentKey(experiment, featureFlag);
  if (!experimentKey) {
    return undefined;
  }

  const statsig: ShowClause[`statsig`] = {
    control_key: controlKey,
    confidence: experiment.settings?.confidenceLevel,
    exposures,
  };
  if (experiment.settings?.testingModel === ExperimentTestingModel.SEQUENTIAL) {
    statsig.sequential_testing_adjustment = {};
  }

  const show: ShowClause[] = [
    {
      ...metric.definition,
      statsig,
    },
  ];

  return {
    sections: {
      cohorts: [],
      formula: [],
      show,
      filter: [...(metric.filters || []), ...additionalFilters],
      globalDataGroupId: metric.dataGroupId,
      group: buildExperimentGroup(experiment, featureFlag),
      time: buildExperimentTime(experiment),
    },
    sorting: _buildExperimentSorting(),
    columnWidths: {
      bar: {},
    },
    displayOptions: {
      chartType: ChartType.Metric,
    },
    metricId: metric.id,
  };
}

export function buildExperimentGroup(experiment: Experiment, featureFlag?: FeatureFlagApiResponse): GroupClause[] {
  const experimentKey = _getExperimentKey(experiment, featureFlag);
  if (!experimentKey) {
    return [];
  }
  return _buildExperimentGroup(experimentKey);
}

function _buildExperimentGroup(experimentKey: string): GroupClause[] {
  return [
    {
      dataset: DATASET_MIXPANEL,
      value: `${EXPERIMENT_PROPERTY_PREFIX}${experimentKey}`,
      resourceType: InsightsResourceType.Events,
      profileType: undefined,
      search: ``,
      dataGroupId: undefined,
      propertyType: PropertyType.String,
      typeCast: undefined,
      unit: undefined,
    },
  ];
}

function _buildExperimentSorting(): InsightsSortConfig {
  return _getDefaultInsightsSorting({numSegmentColumns: 0, canUseViewN: true});
}

export function buildExperimentTime(experiment: Experiment): TimeClause[] {
  return [
    experiment.end_date
      ? {
          //@ts-expect-error no idea why but the type param always fails here
          dateRangeType: DATE_RANGE_TYPE.BETWEEN,
          unit: TIME_UNITS.DAY,
          value: [
            moment(experiment.start_date || undefined).format(TIME_CLAUSE_DATE_FORMAT),
            moment(experiment.end_date || undefined).format(TIME_CLAUSE_DATE_FORMAT),
          ],
        }
      : {
          dateRangeType: DATE_RANGE_TYPE.SINCE,
          unit: TIME_UNITS.DAY,
          value: [moment(experiment.start_date || undefined).format(TIME_CLAUSE_DATE_FORMAT)],
        },
  ];
}

function _getExperimentKey(experiment: Experiment, featureFlag?: FeatureFlagApiResponse): string | undefined {
  if (experiment?.settings?.collectionMethod === ExperimentCollectionType.EXPOSURE_EVENTS) {
    return experiment.settings?.exposureEventSettings?.experimentName;
  }
  if (experiment?.settings?.collectionMethod === ExperimentCollectionType.FEATURE_FLAG) {
    return featureFlag?.key;
  }
  return undefined;
}

type StatisticalSignificanceParams = {
  lift: number;
  liftConfidence: number;
};
export const evalStatisticalSignificance = ({lift, liftConfidence}: StatisticalSignificanceParams) => {
  if (lift - liftConfidence > 0) {
    return StatisticallySignificant.YES_POSITIVE;
  }
  if (lift + liftConfidence < 0) {
    return StatisticallySignificant.YES_NEGATIVE;
  }
  return StatisticallySignificant.NO;
};

type TransformStatsigResultsParams = {
  results: StatsigResults;
  controlKey: string;
  treatmentKey: string;
  hideSignificance?: boolean;
};
export function transformStatsigResults({
  results,
  controlKey,
  treatmentKey,
  hideSignificance = false,
}: TransformStatsigResultsParams): ExperimentMetricResults | undefined {
  const treatmentData = results[treatmentKey];
  const controlData = results[controlKey];
  if (!treatmentData || !controlData) {
    return undefined;
  }

  if (typeof controlData === `number` || typeof treatmentData === `number`) {
    return undefined;
  }

  const control = (controlData[`raw_data`] ?? controlData[`value`] ?? controlData[`all`]) as number;
  const treatment = (treatmentData[`raw_data`] ?? treatmentData[`value`] ?? treatmentData[`all`]) as number;
  const notSupported = Object.hasOwn(controlData, `all`) && !Object.hasOwn(controlData, `value`);

  let lift: number;
  let liftConfidence: number;
  if (control || !treatment) {
    // we can use the provided lift
    lift = (treatmentData.lift as number) ?? NaN;
    liftConfidence = (treatmentData?.lift_margin_of_error as number) ?? NaN;
  } else {
    // we have infinite lift
    lift = treatment > 0 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    liftConfidence = 0;
  }

  return {
    control,
    controlFormat: String(controlData[`format`]) as ExperimentMetricResultsFormat,
    treatment,
    treatmentFormat: String(treatmentData[`format`]) as ExperimentMetricResultsFormat,
    lift,
    liftConfidence,
    pValue: treatmentData.p_value as number,
    statisticallySignificant:
      !hideSignificance && String(treatmentData.is_significant) === `True`
        ? evalStatisticalSignificance({lift, liftConfidence})
        : StatisticallySignificant.NO,
    notSupported,
  };
}

export function getLatestSeriesValues(queryResults: InsightsQueryResults) {
  if (!queryResults) {
    return {};
  }
  const seriesMap = (queryResults.series ?? {}) as {[seriesKey: string]: InsightsSeriesTree<number>};
  const series = Object.values(seriesMap)[0] ?? {};

  return mapValues(series, (value) => last(Object.values(value)) ?? 0);
}

export function getLatestOverallSeriesValue(queryResults: InsightsQueryResults): number {
  return getLatestSeriesValues(queryResults)[RESULTS_OVERALL_SUM_KEY] ?? 0;
}
